import React, { useEffect, useRef, useState } from 'react';
import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import {
  LayoutDashboard,
  Container as ContainerIcon,
  FileText,
  Receipt,
  Map,
  Tag,
  Sparkles,
  ShieldCheck,
  Settings,
  ChevronUp,
  LogOut,
  LifeBuoy,
  MessageSquare,
  Compass,
  Calculator,
  type LucideIcon,
} from 'lucide-react';
import { useLanguage } from '@/contexts/LanguageContext';
import { useAuth } from '@/hooks/useAuth';
import { useDirection } from '@/contexts/DirectionContext';
import { BrandLogo } from '@/components/BrandLogo';
import { useOnboardingTour } from '@/features/help/tour/OnboardingTourProvider';

type NavItemDef = {
  id: string;
  to?: string;
  icon: LucideIcon;
  labelKey: string;
  pillKey?: string;
  disabled?: boolean;
  badge?: number;
};

const MAIN_ITEMS: NavItemDef[] = [
  { id: 'dashboard', to: '/app/dashboard', icon: LayoutDashboard, labelKey: 'nav.dashboard' },
  { id: 'containers', to: '/app/containers', icon: ContainerIcon, labelKey: 'nav.containers' },
  { id: 'bills', to: '/app/bills', icon: FileText, labelKey: 'nav.bills' },
  { id: 'invoices', icon: Receipt, labelKey: 'nav.invoices', pillKey: 'nav.invoices.pill', disabled: true },
  { id: 'map', to: '/app/map', icon: Map, labelKey: 'nav.map' },
  { id: 'offers', to: '/app/offers', icon: Tag, labelKey: 'nav.offers' },
  { id: 'costs', to: '/app/costs', icon: Calculator, labelKey: 'nav.costs' },
  { id: 'assistant', to: '/app/ai-chat', icon: Sparkles, labelKey: 'nav.assistant', pillKey: 'nav.assistant.pill' },
];

const ACCOUNT_ITEMS: NavItemDef[] = [
  { id: 'two_factor', to: '/app/two-factor', icon: ShieldCheck, labelKey: 'nav.two_factor' },
  { id: 'settings', to: '/app/settings', icon: Settings, labelKey: 'nav.settings' },
  { id: 'help', to: '/app/help', icon: LifeBuoy, labelKey: 'nav.help' },
  { id: 'contact', to: '/app/contact', icon: MessageSquare, labelKey: 'nav.contact' },
];

interface AppSidebarProps {
  collapsed: boolean;
  mobileOpen: boolean;
  onCloseMobile: () => void;
}

export function AppSidebar({ collapsed, mobileOpen, onCloseMobile }: AppSidebarProps) {
  const { t } = useLanguage();
  const { direction } = useDirection();
  // AI și Costuri sunt doar pe import (tool-urile filtrează Import CIF/FOB; tarifele de terminal
  // și cheltuielile locale se aplică la descărcarea în Constanța).
  const mainItems =
    direction === 'export'
      ? MAIN_ITEMS.filter((it) => it.id !== 'assistant' && it.id !== 'costs')
      : MAIN_ITEMS;

  return (
    <aside className={'sidebar' + (mobileOpen ? ' mobile-open' : '')}>
      <div className="sidebar-brand">
        <BrandLogo className="brand-logo" />
      </div>

      <nav className="sidebar-nav" data-tour="sidebar-nav">
        <div className="nav-section-label">{t('nav.main')}</div>
        {mainItems.map((it) => (
          <SidebarNavItem
            key={it.id}
            item={it}
            collapsed={collapsed}
            onNavigate={onCloseMobile}
          />
        ))}

        <div className="nav-section-label">{t('nav.account')}</div>
        {ACCOUNT_ITEMS.map((it) => (
          <SidebarNavItem
            key={it.id}
            item={it}
            collapsed={collapsed}
            onNavigate={onCloseMobile}
          />
        ))}
      </nav>

      <UserCard collapsed={collapsed} />
    </aside>
  );
}

function SidebarNavItem({
  item,
  collapsed,
  onNavigate,
}: {
  item: NavItemDef;
  collapsed: boolean;
  onNavigate: () => void;
}) {
  const { t } = useLanguage();
  const location = useLocation();
  const Icon = item.icon;
  const label = t(item.labelKey);

  if (item.disabled || !item.to) {
    return (
      <button
        type="button"
        className={'nav-item' + (item.disabled ? ' disabled' : '')}
        title={collapsed ? label : undefined}
        disabled={item.disabled}
      >
        <Icon size={18} strokeWidth={2} />
        <span className="label">{label}</span>
        {item.pillKey ? <span className="pill">{t(item.pillKey)}</span> : null}
        {!item.disabled && item.badge ? <span className="badge">{item.badge}</span> : null}
      </button>
    );
  }

  const active = location.pathname.startsWith(item.to);

  return (
    <NavLink
      to={item.to}
      onClick={onNavigate}
      className={'nav-item' + (active ? ' active' : '')}
      title={collapsed ? label : undefined}
      data-tour={item.id}
    >
      <Icon size={18} strokeWidth={2} />
      <span className="label">{label}</span>
      {item.pillKey ? <span className="pill">{t(item.pillKey)}</span> : null}
      {item.badge ? <span className="badge">{item.badge}</span> : null}
    </NavLink>
  );
}

function UserCard({ collapsed }: { collapsed: boolean }) {
  const { user, logout, getUserInitials } = useAuth();
  const { t } = useLanguage();
  const navigate = useNavigate();
  const { startTour } = useOnboardingTour();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  if (!user) return null;

  const initials = getUserInitials ? getUserInitials() : (user.fullName || user.email || '?').slice(0, 2).toUpperCase();

  return (
    <div className="sidebar-footer" ref={ref}>
      <button
        type="button"
        className={'user-card user-card-btn' + (open ? ' open' : '')}
        onClick={() => setOpen((v) => !v)}
        title={collapsed ? user.fullName : undefined}
      >
        <div className="user-avatar">{initials}</div>
        <div className="user-meta">
          <div className="name">{user.fullName}</div>
          <div className="role">{user.email}</div>
        </div>
        {!collapsed && <ChevronUp size={14} className="user-card-chev" />}
      </button>
      {open && (
        <div className="user-popover">
          <div className="user-popover-head">
            <div className="user-avatar">{initials}</div>
            <div style={{ minWidth: 0 }}>
              <div style={{ fontWeight: 600, fontSize: 13 }}>{user.fullName}</div>
              <div
                className="muted fs-12"
                style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}
              >
                {user.email}
              </div>
            </div>
          </div>
          <div className="dropdown-divider" />
          <button
            type="button"
            className="dropdown-item user-popover-item"
            onClick={() => {
              setOpen(false);
              startTour();
            }}
          >
            <Compass size={15} />
            <span style={{ flex: 1 }}>{t('help.tour.replay')}</span>
          </button>
          <button
            type="button"
            className="dropdown-item user-popover-item"
            onClick={() => {
              setOpen(false);
              navigate('/app/help');
            }}
          >
            <LifeBuoy size={15} />
            <span style={{ flex: 1 }}>{t('nav.help')}</span>
          </button>
          <div className="dropdown-divider" />
          <button
            type="button"
            className="dropdown-item user-popover-item"
            onClick={() => {
              setOpen(false);
              logout();
            }}
          >
            <LogOut size={15} />
            <span style={{ flex: 1 }}>{t('nav.logout')}</span>
          </button>
        </div>
      )}
    </div>
  );
}
